import { useEffect, useRef, useState, useCallback } from 'react';
import { getEventsWebSocketUrl } from '../lib/api';

/**
 * useAgentFeed — live subscription to Adam event stream via WebSocket.
 *
 * Usage:
 *   const { connected, events, lastEvent } = useAgentFeed();
 *
 * Events are pushed as Adam emits them (signal.discovered, candidate.hot, etc).
 */
export function useAgentFeed(eventTypes = null) {
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const connect = useCallback(() => {
    try {
      const url = getEventsWebSocketUrl();
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        setConnected(true);
        if (eventTypes) {
          ws.send(JSON.stringify({ type: 'subscribe', event_types: eventTypes }));
        }
      });

      ws.addEventListener('message', (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'keepalive' || msg.type === 'pong' || msg.type === 'connected' || msg.type === 'subscribed') {
            return;
          }
          setLastEvent(msg);
          setEvents((prev) => [msg, ...prev].slice(0, 100));
        } catch {}
      });

      ws.addEventListener('close', () => {
        setConnected(false);
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, 3000);
        }
      });

      ws.addEventListener('error', () => {
        setConnected(false);
      });
    } catch (e) {
      // Silent — will auto-reconnect
    }
  }, [eventTypes]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
      }
    };
    // eslint-disable-next-line
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  return { connected, events, lastEvent, sendPing };
}
